// src/user/VideoUpload.js
import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  VideoLibrary,
  Category,
  Description,
  Upload,
  CheckCircle,
  Error,
  CloudUpload,
  HighQuality,
} from '@mui/icons-material';
import api from '../utils/api';
import { API } from '../config/apiConfig';
import { jwtDecode } from 'jwt-decode';
import videoConverter from '../utils/videoConverter';

function VideoUpload() {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [videoFile, setVideoFile] = useState(null);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  
  // Channel selection
  const [userChannels, setUserChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [selectedChannelName, setSelectedChannelName] = useState('');
  const [channelsLoading, setChannelsLoading] = useState(true);

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [statusMsg, setStatusMsg] = useState(''); // Separate status message (not an error)
  const [uploadProgress, setUploadProgress] = useState(0);
  const [conversionProgress, setConversionProgress] = useState(0);
  const [isConverting, setIsConverting] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogMessage, setSuccessDialogMessage] = useState('');
  const [originalResolution, setOriginalResolution] = useState(null); // Store original video resolution

  const mapFriendlyError = (raw) => {
    if (!raw) return '❌ Video upload failed. Please try again.';
    const lower = String(raw).toLowerCase();
    if (
      lower.includes('video validation failed') ||
      (lower.includes('validation') && lower.includes('video')) ||
      lower.includes('resolution') ||
      (lower.includes('height') && lower.includes('360'))
    ) {
      return 'Video cannot be greater than 480p. Please export to 360p or 480p (height ≤ 480px) and try again.';
    }
    if (
      lower.includes('ffprobe') ||
      lower.includes('failed to read video metadata') ||
      (lower.includes('metadata') && lower.includes('video')) ||
      lower.includes('ffmpeg')
    ) {
      return 'We could not verify your video resolution. Please export to 360p or 480p (height ≤ 480px) and try again.';
    }
    if (lower.includes('file too large') || lower.includes('exceeds 2gb') || lower.includes('max file size')) {
      return 'Your video file is too large. Maximum allowed size is 2GB.';
    }
    if (lower.includes('network')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    return raw;
  };

  // ✅ Fetch user channels
  useEffect(() => {
    const fetchUserChannels = async () => {
      try {
        setChannelsLoading(true);
        console.log('📺 Fetching user channels...');
        
        const res = await api.get(API.myChannels);
        console.log('📺 Channels API Response:', res.data);
        
        if (res.data?.success && res.data?.channels) {
          setUserChannels(res.data.channels);
          
          // Auto-select first channel if available
          if (res.data.channels.length > 0) {
            setSelectedChannelId(res.data.channels[0].ChannelId);
            setSelectedChannelName(res.data.channels[0].ChannelName);
          }
          
          console.log('✅ User channels loaded:', res.data.channels.length);
        } else {
          console.log('ℹ️ User has no channels yet');
          setUserChannels([]);
        }
      } catch (err) {
        console.error('❌ Failed to fetch user channels:', err);
        if (err.response?.status !== 404) {
          console.error('❌ Unexpected error fetching channels');
        }
        setUserChannels([]);
      } finally {
        setChannelsLoading(false);
      }
    };
    fetchUserChannels();
  }, []);

  // ✅ Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        setErrorMsg(''); // Clear any previous errors
        
        console.log('🔍 Fetching categories from API...');
        console.log('🔍 API Base URL:', api.defaults.baseURL);
        console.log('🔍 Full URL will be:', api.defaults.baseURL + '/categories');
        
        const res = await api.get('/categories');
        
        console.log('📡 Categories API Response Status:', res.status);
        console.log('📡 Categories API Response Headers:', res.headers);
        console.log('📡 Categories API Data:', res.data);
        
        if (res.data?.success && res.data?.categories) {
          // Data is already in the correct format from the API
          setCategories(res.data.categories);
          console.log('✅ Categories loaded:', res.data.categories.length);
          console.log('📋 Categories data:', res.data.categories);
        } else {
          console.log('❌ Invalid response structure:', res.data);
          setErrorMsg('Invalid categories response from server.');
        }
      } catch (err) {
        console.error('❌ Failed to fetch categories:', err);
        console.error('❌ Error details:', err.response?.data || err.message);
        
        // More specific error message
        let errorMessage = 'Failed to load categories. ';
        if (err.response?.status === 401) {
          errorMessage += 'Please login again.';
        } else if (err.response?.status === 500) {
          errorMessage += 'Server error. Please try again later.';
        } else if (err.code === 'NETWORK_ERROR') {
          errorMessage += 'Network error. Check your connection.';
        } else {
          errorMessage += 'Please refresh the page.';
        }
        setErrorMsg(errorMessage);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // ✅ Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!category) {
        setSubcategories([]);
        setSubcategory('');
        return;
      }

      try {
        setSubcategoriesLoading(true);
        // Find the category ID
        const selectedCategory = categories.find(cat => cat.Category === category);
        if (!selectedCategory?.VideoCategoryId) {
          console.log('❌ No VideoCategoryId found for category:', category);
          console.log('❌ Available categories:', categories.map(c => ({ name: c.Category, id: c.VideoCategoryId })));
          setSubcategories([]);
          setSubcategory('');
          return;
        }

        console.log('🔍 Fetching subcategories for category ID:', selectedCategory.VideoCategoryId);
        const res = await api.get(`/categories/${selectedCategory.VideoCategoryId}/subcategories`);
        
        if (res.data?.success && res.data?.subcategories) {
          setSubcategories(res.data.subcategories);
          console.log('✅ Subcategories loaded:', res.data.subcategories.length);
        } else {
          setSubcategories([]);
          console.log('❌ No subcategories found in response');
        }
      } catch (err) {
        console.error('❌ Failed to fetch subcategories:', err);
        setSubcategories([]);
      } finally {
        setSubcategoriesLoading(false);
      }
      
      // Reset subcategory selection when category changes
      setSubcategory('');
    };

    fetchSubcategories();
  }, [category, categories]);

  // ✅ Handle file selection (no duration constraints)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setErrorMsg('');
    setSuccessMsg('');
    
    // Validate file size immediately
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB
    if (file.size > maxSize) {
      setErrorMsg(`File is too large (${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB). Maximum allowed size is 2GB.`);
      setVideoFile(null);
      return;
    }

    // Warn for large files but allow
    const warningSize = 1.5 * 1024 * 1024 * 1024; // 1.5GB
    if (file.size > warningSize) {
      setErrorMsg(`⚠️ Large file detected (${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB). Conversion may take longer. Please ensure you have a stable internet connection.`);
    }

    // Check if file is empty
    if (file.size === 0) {
      setErrorMsg('File is empty. Please select a valid video file.');
      setVideoFile(null);
      return;
    }

    setVideoFile(file);
    setUploadProgress(0);
    setConversionProgress(0);
    setIsConverting(false);
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !videoFile || !category) {
      setErrorMsg('Title, category, and video file are required.');
      return;
    }

    if (!selectedChannelId) {
      setErrorMsg('Please select a channel to upload to.');
      return;
    }

    // Get user ID from JWT token
    const token = localStorage.getItem('token');
    if (!token) {
      setErrorMsg('Please login first.');
      return;
    }

    const decoded = jwtDecode(token);
    const userId = decoded.userId;

    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    setUploadProgress(0);
    setConversionProgress(0);
    setIsConverting(false);
    setOriginalResolution(null); // Reset resolution

    let convertedVideoFile = videoFile;

    try {
      // Step 1: Detect original resolution first
      try {
        const resolution = await videoConverter.detectVideoResolution(videoFile);
        if (resolution) {
          setOriginalResolution(resolution);
          console.log(`📐 Original resolution: ${resolution.width}x${resolution.height}`);
        }
      } catch (err) {
        console.warn('⚠️ Could not detect resolution:', err);
      }

      // Step 2: Convert video to 480p H.264 - REQUIRED before upload
      try {
        setIsConverting(true);
        setConversionProgress(0);
        setErrorMsg(''); // Clear any previous errors
        setStatusMsg(''); // Clear status messages
        console.log('🎬 Starting video conversion to 480p H.264...');
        console.log('📁 Original file:', videoFile.name);
        console.log('📊 Original size:', (videoFile.size / (1024 * 1024)).toFixed(2), 'MB');

        // Show initial loading message (as status, not error)
        const fileSizeMB = videoFile.size / (1024 * 1024);
        if (fileSizeMB > 500) {
          setStatusMsg('⏱️ Large file detected. Conversion may take 15-30 minutes. Please wait...');
        } else if (fileSizeMB > 100) {
          setStatusMsg('⏱️ Medium file detected. Conversion may take 5-15 minutes. Please wait...');
        } else {
          setStatusMsg('⏱️ Loading FFmpeg and converting video. This may take a few minutes...');
        }

        // Convert video with progress tracking (only converts if > 480p)
        const convertedBlob = await videoConverter.convertTo480p(
          videoFile,
          (progress) => {
            const progressPercent = Math.round(progress * 100);
            setConversionProgress(progressPercent);
            console.log(`📊 Conversion progress: ${progressPercent}%`);
            
            // Update status message (not error message) to show progress
            if (progressPercent < 10) {
              setStatusMsg('⏱️ Loading FFmpeg engine...');
            } else if (progressPercent < 30) {
              setStatusMsg(`⏱️ Converting video... ${progressPercent}%`);
            } else if (progressPercent < 70) {
              setStatusMsg(`⏱️ Processing video frames... ${progressPercent}%`);
            } else if (progressPercent < 95) {
              setStatusMsg(`⏱️ Finalizing conversion... ${progressPercent}%`);
            } else {
              setStatusMsg(`⏱️ Almost done... ${progressPercent}%`);
            }
          }
        );
        
        // Check if conversion was actually performed or original file was used
        const wasConverted = convertedBlob.size !== videoFile.size || convertedBlob.size < videoFile.size;
        if (!wasConverted && convertedBlob.size === videoFile.size) {
          console.log('✅ No conversion needed - using original file (already ≤ 480p)');
          setStatusMsg('✅ Video is already ≤ 480p - using original file');
          // Skip conversion progress display since no conversion happened
          setIsConverting(false);
          setConversionProgress(100);
        } else {
          // Clear status message after successful conversion
          setStatusMsg('');
        }

        // Create a File object from the blob
        convertedVideoFile = new File(
          [convertedBlob],
          `converted_${videoFile.name.replace(/\.[^/.]+$/, '')}.mp4`,
          { type: 'video/mp4' }
        );

        // Log conversion result
        if (convertedVideoFile.size < videoFile.size) {
          const reductionPercent = ((1 - convertedVideoFile.size / videoFile.size) * 100).toFixed(1);
          console.log(`✅ Video converted successfully! Size reduced by ${reductionPercent}%`);
        } else if (convertedVideoFile.size === videoFile.size) {
          console.log('✅ Using original file (already ≤ 480p, no conversion needed)');
        } else {
          console.warn('⚠️ Converted file is larger than original (this should be rare)');
        }
        console.log('📊 Converted size:', (convertedVideoFile.size / (1024 * 1024)).toFixed(2), 'MB');
        console.log('📁 Converted file name:', convertedVideoFile.name);

        setIsConverting(false);
        setConversionProgress(100);

        // Small delay to show conversion complete
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (conversionError) {
        console.error('❌ Video conversion error:', conversionError);
        setIsConverting(false);
        setConversionProgress(0);
        setLoading(false);
        
        // Extract error message properly
        let errorMessage = '';
        
        if (conversionError && conversionError.message) {
          // If error message already contains "Video conversion failed", use it as is
          if (conversionError.message.includes('Video conversion failed')) {
            errorMessage = conversionError.message;
          } else {
            errorMessage = `Video conversion failed: ${conversionError.message}`;
          }
        } else if (conversionError && typeof conversionError === 'string') {
          errorMessage = `Video conversion failed: ${conversionError}`;
        } else if (conversionError && conversionError.toString) {
          const errorStr = conversionError.toString();
          if (errorStr !== '[object Object]') {
            errorMessage = `Video conversion failed: ${errorStr}`;
          } else {
            errorMessage = 'Video conversion failed. The file may be corrupted, too large, or in an unsupported format. Please try a different video file.';
          }
        } else {
          errorMessage = 'Video conversion failed. The file may be corrupted, too large, or in an unsupported format. Please try a different video file.';
        }
        
        setErrorMsg(errorMessage);
        return; // Stop upload if conversion fails
      }

      // Step 2: Upload the converted video
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('categoryName', category);
      
      // Add subcategory if selected
      if (subcategory) {
        formData.append('subcategoryName', subcategory);
      }
      
      // Add channel ID
      if (selectedChannelId) {
        formData.append('channelId', selectedChannelId);
      }
      
      // Use converted video file, not original
      formData.append('video', convertedVideoFile);

      // Create axios config with progress tracking - NO TIMEOUT
      const config = {
        headers: { 
          'Content-Type': 'multipart/form-data',
        },
        timeout: 0, // NO TIMEOUT - wait indefinitely for upload
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            console.log(`📊 Upload progress: ${percentCompleted}%`);
          setUploadProgress(percentCompleted);
          } else {
            // If total is not available, estimate based on loaded bytes
            const estimatedProgress = Math.min(
              Math.round((progressEvent.loaded / (videoFile.size || 1)) * 100),
              99
            );
            console.log(`📊 Upload progress (estimated): ${estimatedProgress}%`);
            setUploadProgress(estimatedProgress);
          }
        },
      };

      const res = await api.post(API.videoUpload, formData, config);

      console.log('📦 Upload response:', res.data);
      
      // Set upload progress to 100% when complete
      setUploadProgress(100);
      
      // Build success message - always show "Video uploaded successfully!"
      const processingInfo = res.data?.processing;
      let message = 'Video uploaded successfully!';
      let details = [];
      
      // Add file sizes (original and converted) - always show these
      const originalSize = formatFileSize(videoFile.size);
      const convertedSize = formatFileSize(convertedVideoFile.size);
      
      // Format resolution (e.g., "720P", "1080P")
      let resolutionText = '';
      if (originalResolution && originalResolution.height) {
        resolutionText = `${originalResolution.height}P`;
      }
      
      // Show original size with resolution
      if (resolutionText) {
        details.push(`Original size: ${originalSize}, ${resolutionText}`);
      } else {
        details.push(`Original size: ${originalSize}`);
      }
      details.push(`Converted size: ${convertedSize}`);
      
      // Check if server returned uploadId (async processing)
      if (res.data?.uploadId) {
        // Server is processing asynchronously - that's fine, upload was successful
        message = 'Video uploaded successfully!';
        details.push('Your video is being processed and will be available shortly.');
      } else {
        // Immediate success
        message = 'Video uploaded successfully!';
      }
      
      // Add additional info if available
      if (processingInfo?.fileSize) {
        // Don't add if we already have converted size
        if (!details.some(d => d.includes('Converted size'))) {
          details.push(`File size: ${processingInfo.fileSize}`);
        }
      }
      
      if (processingInfo?.status) {
        details.push(`Status: ${processingInfo.status}`);
      }
      
      if (res.data?.videoId) {
        details.push(`Video ID: ${res.data.videoId}`);
      }
      
      // Show success dialog with proper success message
      setSuccessDialogMessage({
        title: message,
        details: details.length > 0 ? details.join('\n') : null,
      });
      setShowSuccessDialog(true);
      
      // Also set inline success message
      setSuccessMsg(message);
      console.log('✅ Upload successful - Success message set:', message);
      
      // Reset form after a brief delay
      setTimeout(() => {
      setTitle('');
      setDescription('');
      setCategory('');
      setSubcategory('');
      setVideoFile(null);
      setUploadProgress(0);
      setConversionProgress(0);
      setIsConverting(false);
      }, 500);
    } catch (err) {
      console.error('❌ Upload failed:', err.response?.data || err.message);
      // Reset progress on error
      setUploadProgress(0);
      setConversionProgress(0);
      setIsConverting(false);
      const rawMessage =
        err.response?.data?.error ||
          err.response?.data?.message ||
        (typeof err.response?.data === 'string' ? err.response?.data : JSON.stringify(err.response?.data)) ||
        err.message ||
        '❌ Video upload failed. Please try again.';
      setErrorMsg(mapFriendlyError(rawMessage));
    } finally {
      setLoading(false);
      setIsConverting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <VideoLibrary sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Video Upload
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Share your content with the world
          </Typography>
        </CardContent>
      </Card>

      {/* Error Messages - Only show actual errors, not status messages */}
      {errorMsg && !isConverting && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          icon={<Error />}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  setErrorMsg('');
                  // Retry fetching categories
                  const fetchCategories = async () => {
                    try {
                      setCategoriesLoading(true);
                      const res = await api.get('/categories');
                      if (res.data?.success && res.data?.categories) {
                        setCategories(res.data.categories);
                        console.log('✅ Categories loaded on retry:', res.data.categories.length);
                      }
                    } catch (err) {
                      console.error('❌ Retry failed:', err);
                      setErrorMsg('Retry failed. Please check your connection and try again.');
                    } finally {
                      setCategoriesLoading(false);
                    }
                  };
                  fetchCategories();
                }}
              >
                Retry
              </Button>
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setErrorMsg('')}
              >
                ×
              </IconButton>
            </Stack>
          }
        >
          {errorMsg}
        </Alert>
      )}
      
      {successMsg && !showSuccessDialog && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 2,
            fontSize: '1rem',
            fontWeight: 500,
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 500,
            }
          }} 
          icon={<CheckCircle sx={{ fontSize: 28 }} />}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setSuccessMsg('')}
            >
              ×
            </IconButton>
          }
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {successMsg}
          </Typography>
        </Alert>
      )}

      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          setSuccessMsg('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <Stack alignItems="center" spacing={2}>
            <CheckCircle sx={{ fontSize: 64, color: 'white' }} />
            <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
              {successDialogMessage.title || 'Upload Successful!'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pb: 2, textAlign: 'center' }}>
          <DialogContentText sx={{ color: 'white', fontSize: '1rem', mb: 2 }}>
            Your video has been uploaded successfully and is being processed.
          </DialogContentText>
          {successDialogMessage.details && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                textAlign: 'left',
              }}
            >
              <Typography variant="body2" sx={{ color: 'white', whiteSpace: 'pre-line' }}>
                {successDialogMessage.details}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
          <Button
            onClick={() => {
              setShowSuccessDialog(false);
              setSuccessMsg('');
            }}
            variant="contained"
            sx={{
              minWidth: 120,
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            Great!
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Form */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Channel Selection Section - FIRST */}
              {userChannels.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VideoLibrary color="primary" />
                    Select Channel *
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'primary.light',
                    bgcolor: 'primary.50',
                  }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {userChannels.map((channel) => (
                        <Chip
                          key={channel.ChannelId}
                          label={channel.ChannelName}
                          onClick={() => {
                            setSelectedChannelId(channel.ChannelId);
                            setSelectedChannelName(channel.ChannelName);
                          }}
                          color={selectedChannelId === channel.ChannelId ? 'primary' : 'default'}
                          variant={selectedChannelId === channel.ChannelId ? 'filled' : 'outlined'}
                          clickable
                          icon={<VideoLibrary />}
                        />
                      ))}
                    </Stack>
                    {selectedChannelName && (
                      <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
                        Selected: <strong>{selectedChannelName}</strong>
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* No channels message */}
              {userChannels.length === 0 && !channelsLoading && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>No channels found</strong>
                  </Typography>
                  <Typography variant="caption">
                    You must create a channel first to upload videos.
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ mt: 1 }}
                    onClick={() => window.location.href = '/dashboard/channels'}
                  >
                    Create Channel
                  </Button>
                </Alert>
              )}

              {/* Category and Subcategory Selection - Split Layout */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Category color="primary" />
                  Category & Subcategory Selection
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {/* Left Side - Category Selection */}
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Main Category
                      </Typography>
                      <TextField
                        select
                        label="Select Category"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        fullWidth
                        required
                        disabled={categoriesLoading}
                        InputProps={{
                          startAdornment: categoriesLoading ? <CircularProgress size={20} /> : <Typography sx={{ mr: 1 }}>🏷️</Typography>
                        }}
                      >
                        <MenuItem value="">
                          {categoriesLoading ? 'Loading categories...' : 'Select a category'}
                        </MenuItem>
                        {categories.map((c) => (
                          <MenuItem
                            key={c.VideoCategoryId || c.CategoryID || c.id}
                            value={c.Category}
                          >
                            {c.Category}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Grid>

                  {/* Right Side - Subcategory Selection */}
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        Subcategory
                        {subcategoriesLoading && <CircularProgress size={16} />}
                      </Typography>
                      
                      {category ? (
                        subcategories.length > 0 ? (
                          <>
                            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                              📂 {subcategories.length} available
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                              {/* No subcategory option */}
                              <Chip
                                label="No subcategory"
                                onClick={() => setSubcategory('')}
                                color={subcategory === '' ? 'primary' : 'default'}
                                variant={subcategory === '' ? 'filled' : 'outlined'}
                                clickable
                                icon={<Category />}
                                size="small"
                              />
                              
                              {/* Subcategory options */}
                              {subcategories.map((sub) => (
                                <Chip
                                  key={sub.SubcategoryId}
                                  label={sub.SubcategoryName}
                                  onClick={() => setSubcategory(sub.SubcategoryName)}
                                  color={subcategory === sub.SubcategoryName ? 'primary' : 'default'}
                                  variant={subcategory === sub.SubcategoryName ? 'filled' : 'outlined'}
                                  clickable
                                  size="small"
                                />
                              ))}
                            </Box>
                            
                            {/* Selected subcategory display */}
                            {subcategory && (
                              <Alert severity="info" icon={<CheckCircle />} sx={{ mt: 1 }}>
                                Selected: <strong>{subcategory}</strong>
                              </Alert>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            No subcategories available for this category
                          </Typography>
                        )
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          Select a category first
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Video Information Section - MOVED TO THIRD */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Description color="primary" />
                  Video Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Video Title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                      fullWidth
                      placeholder="Enter an engaging title for your video"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>📝</Typography>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={3}
                      fullWidth
                      placeholder="Describe your video content (optional)"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }}>📄</Typography>
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Video Requirements Info */}
              <Alert severity="info" icon={false}>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    📋 Video Requirements
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'green' }}>
                    • Please upload only 360p or 480p in (H.264) video for faster Upload & Stream worldwide
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'red' }}>
                    • Higher Resolution Videos will be converted to 480p H.264 before upload, which may takes time<br/>
                    • Maximum file size: 2GB
                  </Typography>
                </Stack>
              </Alert>

              {/* Video File Upload Section */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudUpload color="primary" />
                  Video File
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Button 
                  variant="outlined" 
                  component="label" 
                  fullWidth
                  size="large"
                  sx={{ 
                    py: 2, 
                    border: '2px dashed',
                    borderColor: videoFile ? 'primary.main' : 'grey.300',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.50'
                    }
                  }}
                >
                  <Stack alignItems="center" spacing={1}>
                    <CloudUpload sx={{ fontSize: 32, color: videoFile ? 'primary.main' : 'grey.500' }} />
                    {videoFile ? (
                      <>
                        <Typography variant="body1" color="primary">
                          {videoFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(videoFile.size)}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="body1">
                          Choose Video File
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to browse or drag and drop
                        </Typography>
                      </>
                    )}
                  </Stack>
                  <input type="file" hidden accept="video/*" onChange={handleFileChange} />
                </Button>
              </Box>

              {/* Conversion Progress Bar */}
              {isConverting && (
                <Box>
                  <Card 
                    elevation={0}
                    sx={{ 
                      p: 3,
                      border: '1px solid',
                      borderColor: 'warning.light',
                      bgcolor: 'warning.50',
                      borderRadius: 2,
                    }}
                  >
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={24} sx={{ color: 'warning.main' }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
                            Converting Video to 480p...
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                          {conversionProgress}%
                        </Typography>
                      </Box>
                      
                      <Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={conversionProgress} 
                          sx={{ 
                            height: 12, 
                            borderRadius: 6,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 6,
                              background: 'linear-gradient(90deg, #ff9800 0%, #f57c00 100%)',
                              transition: 'all 0.3s ease',
                            }
                          }} 
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', minHeight: '20px' }}>
                        {statusMsg || (
                          <>
                            {conversionProgress === 0 && 'Initializing conversion...'}
                            {conversionProgress > 0 && conversionProgress < 30 && 'Converting video to 480p H.264...'}
                            {conversionProgress >= 30 && conversionProgress < 70 && 'Processing video frames...'}
                            {conversionProgress >= 70 && conversionProgress < 95 && 'Finalizing conversion...'}
                            {conversionProgress >= 95 && conversionProgress < 100 && 'Almost done...'}
                            {conversionProgress === 100 && 'Conversion complete! Starting upload...'}
                          </>
                        )}
                      </Typography>
                      
                      {videoFile && (
                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            File: {videoFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Size: {formatFileSize(videoFile.size)}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Card>
                </Box>
              )}

              {/* Upload Progress Bar and Message */}
              {(loading || uploadProgress > 0) && !isConverting && (
                <Box>
                  <Card 
                    elevation={0}
                    sx={{ 
                      p: 3,
                      border: '1px solid',
                      borderColor: 'primary.light',
                      bgcolor: 'primary.50',
                      borderRadius: 2,
                    }}
                  >
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          {loading && <CircularProgress size={24} />}
                          {!loading && uploadProgress === 100 && (
                            <CheckCircle sx={{ color: 'success.main', fontSize: 24 }} />
                          )}
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {uploadProgress === 100 ? 'Upload Complete!' : 'Uploading Video...'}
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {uploadProgress}%
                        </Typography>
                      </Box>
                      
                      <Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={uploadProgress} 
                          sx={{ 
                            height: 12, 
                            borderRadius: 6,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 6,
                              background: uploadProgress === 100 
                                ? 'linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)'
                                : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                              transition: 'all 0.3s ease',
                            }
                          }} 
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', minHeight: '20px' }}>
                        {uploadProgress === 0 && 'Initializing upload...'}
                        {uploadProgress > 0 && uploadProgress < 10 && 'Starting upload...'}
                        {uploadProgress >= 10 && uploadProgress < 30 && 'Uploading video file...'}
                        {uploadProgress >= 30 && uploadProgress < 70 && 'Uploading in progress...'}
                        {uploadProgress >= 70 && uploadProgress < 95 && 'Almost done...'}
                        {uploadProgress >= 95 && uploadProgress < 100 && 'Finalizing upload...'}
                        {uploadProgress === 100 && 'Upload complete! Processing video...'}
                      </Typography>
                      
                      {videoFile && (
                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            File: {videoFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Size: {formatFileSize(videoFile.size)}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Card>
                </Box>
              )}

              {/* Submit Button */}
              <Button 
                type="submit" 
                variant="contained" 
                size="large"
                disabled={loading || isConverting || !title || !videoFile || !category || !selectedChannelId}
                fullWidth
                sx={{ 
                  py: 2,
                  background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)',
                  }
                }}
              >
                {isConverting ? (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CircularProgress size={24} color="inherit" />
                    <Typography>Converting... {conversionProgress}%</Typography>
                  </Stack>
                ) : loading ? (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CircularProgress size={24} color="inherit" />
                    <Typography>Uploading...</Typography>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Upload />
                    <Typography>Upload Video</Typography>
                  </Stack>
                )}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

export default VideoUpload;
